import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { AppError, errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, Field, Icon, Input, Select, Tabs } from '../../design';
import { useToast } from '../../design/Toast';
import { KioskPreview } from './KioskPreview';
import { LAYOUT_STATUS } from './KioskLayoutsPage';
import {
  type IconName,
  type KioskAction,
  type KioskDoc,
  LANG_LABEL,
  type Lang,
  type Localized,
  PRESETS,
  type Preset,
} from './types';

const ICONS: IconName[] = [
  'locker',
  'box',
  'pickup',
  'doc',
  'gov',
  'key',
  'bed',
  'car',
  'wrench',
  'cart',
  'support',
  'shield',
  'clock',
  'qr',
  'phone',
  'id',
  'check',
  'return',
  'info',
];

type Meta = components['schemas']['KioskLayoutDetail'];

/** Încarcă layoutul și montează editorul cu starea inițializată din document (remontat la fiecare versiune salvată). */
export function KioskLayoutEditorPage() {
  const { id = '' } = useParams();
  const query = useQuery({
    queryKey: ['kiosk-layout', id],
    queryFn: () => unwrap(api.GET('/kiosk-layouts/{id}', { params: { path: { id } } })),
  });
  if (!query.data)
    return <p className="muted">{query.error ? errorMessage(query.error) : 'Se încarcă…'}</p>;
  return <Editor key={`${query.data.id}:${query.data.updatedAt}`} meta={query.data} />;
}

/** Editorul propriu-zis: formular pe document + JSON brut, previzualizare live, publicare. */
function Editor({ meta }: { meta: Meta }) {
  const id = meta.id;
  const { has } = useAuth();
  const canEdit = has('configure');
  const qc = useQueryClient();
  const toast = useToast();
  const initial = meta.document as unknown as KioskDoc;
  const [doc, setDoc] = useState<KioskDoc>(initial);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [json, setJson] = useState(() => JSON.stringify(initial, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [lang, setLang] = useState<string>(initial.defaultLanguage);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [serverErrors, setServerErrors] = useState<{ path?: string; message?: string }[]>([]);

  const save = useMutation({
    mutationFn: (d: KioskDoc) =>
      unwrap(
        api.PUT('/kiosk-layouts/{id}/document', { params: { path: { id } }, body: d as never }),
      ),
    onSuccess: (d) => {
      toast('Documentul a fost salvat; layoutul este draft până la publicare.', 'success');
      setServerErrors([]);
      qc.setQueryData(['kiosk-layout', id], d);
      void qc.invalidateQueries({ queryKey: ['kiosk-layouts'] });
    },
    onError: (e) => {
      if (e instanceof AppError && e.errors.length) setServerErrors(e.errors);
      toast(errorMessage(e), 'error');
    },
  });
  const publish = useMutation({
    mutationFn: () => unwrap(api.POST('/kiosk-layouts/{id}/publish', { params: { path: { id } } })),
    onSuccess: (m) => {
      toast(
        `Publicat ca v${m.version}; lockerele care îl folosesc au o configurație nouă de publicat.`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['kiosk-layout', id] });
      void qc.invalidateQueries({ queryKey: ['kiosk-layouts'] });
      void qc.invalidateQueries({ queryKey: ['lockers'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const rename = useMutation({
    mutationFn: (name: string) =>
      unwrap(api.PATCH('/kiosk-layouts/{id}', { params: { path: { id } }, body: { name } })),
    onSuccess: (m) => {
      qc.setQueryData<Meta>(['kiosk-layout', id], (old) => (old ? { ...old, name: m.name } : old));
      void qc.invalidateQueries({ queryKey: ['kiosk-layouts'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const dirty = JSON.stringify(doc) !== JSON.stringify(meta.document);

  const update = (fn: (d: KioskDoc) => KioskDoc) => {
    setDoc((d) => {
      const n = fn(structuredClone(d));
      setJson(JSON.stringify(n, null, 2));
      return n;
    });
  };
  const setText = (get: (d: KioskDoc) => Localized, value: string) =>
    update((d) => ((get(d)[lang] = value), d));
  const applyJson = () => {
    try {
      const parsed = JSON.parse(json) as KioskDoc;
      setJsonError(null);
      setDoc(parsed);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'JSON invalid');
    }
  };

  return (
    <>
      <div className="flex" style={{ marginBottom: 6 }}>
        <Link to="/layouturi-kiosk" className="flex small">
          <Icon name="chevronLeft" size={14} /> Layouturi kiosk
        </Link>
      </div>
      <div className="flex between wrap" style={{ marginBottom: 16, alignItems: 'flex-start' }}>
        <div>
          <h1
            contentEditable={canEdit}
            suppressContentEditableWarning
            onBlur={(e) =>
              canEdit &&
              e.currentTarget.textContent &&
              e.currentTarget.textContent.trim() !== meta.name &&
              rename.mutate(e.currentTarget.textContent.trim())
            }
          >
            {meta.name}
          </h1>
          <div className="flex wrap muted small" style={{ marginTop: 6 }}>
            <Badge tone={LAYOUT_STATUS[meta.status].tone}>{LAYOUT_STATUS[meta.status].label}</Badge>
            <span>v{meta.version}</span>
            <span>· {meta.assignedLockers} lockere</span>
            {dirty && <Badge tone="orange">modificări nesalvate</Badge>}
          </div>
        </div>
        {canEdit && (
          <div className="flex">
            <Button
              variant="primary"
              onClick={() => save.mutate(doc)}
              busy={save.isPending}
              disabled={!dirty}
            >
              Salvează
            </Button>
            <Button
              icon="check"
              onClick={() => publish.mutate()}
              busy={publish.isPending}
              disabled={dirty || meta.status === 'published' || meta.status === 'archived'}
              title={dirty ? 'Salvează întâi' : undefined}
            >
              Publică
            </Button>
          </div>
        )}
      </div>
      {serverErrors.length > 0 && (
        <ul className="small" style={{ color: 'var(--red)', marginBottom: 12, paddingLeft: 18 }}>
          {serverErrors.map((e, i) => (
            <li key={i}>
              <span className="mono">{e.path}</span> — {e.message}
            </li>
          ))}
        </ul>
      )}
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 440px', gap: 16, alignItems: 'start' }}
      >
        <Card>
          <Tabs
            value={mode}
            onChange={setMode}
            items={[
              { value: 'form', label: 'Formular' },
              { value: 'json', label: 'JSON brut' },
            ]}
          />
          {mode === 'json' ? (
            <div className="stack">
              <textarea
                className="mono small"
                value={json}
                onChange={(e) => setJson(e.target.value)}
                rows={30}
                style={{
                  width: '100%',
                  border: '1px solid var(--line)',
                  borderRadius: 9,
                  padding: 12,
                }}
                disabled={!canEdit}
                aria-label="Document JSON"
              />
              {jsonError && <p style={{ color: 'var(--red)' }}>{jsonError}</p>}
              {canEdit && <Button onClick={applyJson}>Aplică în formular</Button>}
            </div>
          ) : (
            <div className="stack">
              <div className="flex wrap">
                <Field label="Limba editată" htmlFor="kl-lang">
                  <Select id="kl-lang" value={lang} onChange={(e) => setLang(e.target.value)}>
                    {doc.languages.map((l) => (
                      <option key={l} value={l}>
                        {LANG_LABEL[l]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Limbi" hint="Prima este cea implicită">
                  <div className="flex wrap">
                    {(Object.keys(LANG_LABEL) as Lang[]).map((l) => (
                      <label key={l} className="flex small">
                        <input
                          type="checkbox"
                          checked={doc.languages.includes(l)}
                          disabled={
                            !canEdit || (doc.languages.includes(l) && doc.languages.length === 1)
                          }
                          onChange={(e) =>
                            update((d) => {
                              d.languages = e.target.checked
                                ? [...d.languages, l]
                                : d.languages.filter((x) => x !== l);
                              if (!d.languages.includes(d.defaultLanguage))
                                d.defaultLanguage = d.languages[0]!;
                              return d;
                            })
                          }
                        />
                        {LANG_LABEL[l]}
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Implicită" htmlFor="kl-default">
                  <Select
                    id="kl-default"
                    value={doc.defaultLanguage}
                    disabled={!canEdit}
                    onChange={(e) =>
                      update((d) => ((d.defaultLanguage = e.target.value as Lang), d))
                    }
                  >
                    {doc.languages.map((l) => (
                      <option key={l} value={l}>
                        {LANG_LABEL[l]}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <h3>Temă</h3>
              <div className="flex wrap">
                <Field label="Presetare" htmlFor="kl-preset">
                  <Select
                    id="kl-preset"
                    value={doc.theme.custom ? 'custom' : (doc.theme.preset ?? 'office')}
                    disabled={!canEdit}
                    onChange={(e) =>
                      update(
                        (d) => (
                          (d.theme =
                            e.target.value === 'custom'
                              ? {
                                  preset: null,
                                  custom: {
                                    accent: '#1463ff',
                                    accentDark: '#0b46b7',
                                    surfaceA: '#f7faff',
                                    surfaceB: '#eaf2fb',
                                  },
                                }
                              : { preset: e.target.value as Preset, custom: null }),
                          d
                        ),
                      )
                    }
                  >
                    {(Object.keys(PRESETS) as Preset[]).map((p) => (
                      <option key={p} value={p}>
                        {PRESETS[p].label}
                      </option>
                    ))}
                    <option value="custom">Culori proprii</option>
                  </Select>
                </Field>
                {doc.theme.custom &&
                  (['accent', 'accentDark', 'surfaceA', 'surfaceB'] as const).map((k) => (
                    <Field key={k} label={k} htmlFor={`kl-${k}`}>
                      <input
                        id={`kl-${k}`}
                        type="color"
                        value={doc.theme.custom![k]}
                        disabled={!canEdit}
                        onChange={(e) => update((d) => ((d.theme.custom![k] = e.target.value), d))}
                      />
                    </Field>
                  ))}
              </div>

              <h3>Antet și hero</h3>
              <Field label="Produs (antet)" htmlFor="kl-product">
                <Input
                  id="kl-product"
                  value={doc.header.product[lang] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setText((d) => d.header.product, e.target.value)}
                />
              </Field>
              <label className="flex small">
                <input
                  type="checkbox"
                  checked={doc.header.showStatusChip}
                  disabled={!canEdit}
                  onChange={(e) => update((d) => ((d.header.showStatusChip = e.target.checked), d))}
                />
                Arată indicatorul de stare
              </label>
              <div className="flex wrap">
                <Field label="Iconița hero" htmlFor="kl-hero-icon">
                  <Select
                    id="kl-hero-icon"
                    value={doc.hero.icon}
                    disabled={!canEdit}
                    onChange={(e) => update((d) => ((d.hero.icon = e.target.value as IconName), d))}
                  >
                    {ICONS.map((i) => (
                      <option key={i}>{i}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Kicker" htmlFor="kl-kicker">
                  <Input
                    id="kl-kicker"
                    value={doc.hero.kicker[lang] ?? ''}
                    disabled={!canEdit}
                    onChange={(e) => setText((d) => d.hero.kicker, e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Titlu" htmlFor="kl-title">
                <Input
                  id="kl-title"
                  value={doc.hero.title[lang] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setText((d) => d.hero.title, e.target.value)}
                />
              </Field>
              <Field label="Subtitlu" htmlFor="kl-subtitle">
                <Input
                  id="kl-subtitle"
                  value={doc.hero.subtitle[lang] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setText((d) => d.hero.subtitle, e.target.value)}
                />
              </Field>

              <h3>Acțiuni ({doc.actions.length})</h3>
              {doc.actions.map((a, i) => (
                <ActionEditor
                  key={a.id}
                  action={a}
                  lang={lang}
                  canEdit={canEdit}
                  onChange={(next) => update((d) => ((d.actions[i] = next), d))}
                  onRemove={
                    doc.actions.length > 1
                      ? () => update((d) => ((d.actions = d.actions.filter((_, j) => j !== i)), d))
                      : undefined
                  }
                />
              ))}
              {canEdit && doc.actions.length < 4 && (
                <Button
                  small
                  icon="plus"
                  onClick={() =>
                    update((d) => {
                      const base = structuredClone(d.actions[0]!);
                      base.id = `action-${Date.now().toString(36)}`;
                      base.primary = false;
                      d.actions.push(base);
                      return d;
                    })
                  }
                >
                  Adaugă acțiune
                </Button>
              )}

              <h3>Panoul de context</h3>
              <label className="flex small">
                <input
                  type="checkbox"
                  checked={doc.contextPanel.visible}
                  disabled={!canEdit}
                  onChange={(e) => update((d) => ((d.contextPanel.visible = e.target.checked), d))}
                />
                Vizibil
              </label>
              <div className="flex wrap">
                <Field label="Iconiță" htmlFor="kl-ctx-icon">
                  <Select
                    id="kl-ctx-icon"
                    value={doc.contextPanel.icon}
                    disabled={!canEdit}
                    onChange={(e) =>
                      update((d) => ((d.contextPanel.icon = e.target.value as IconName), d))
                    }
                  >
                    {ICONS.map((i) => (
                      <option key={i}>{i}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Etichetă" htmlFor="kl-ctx-tag">
                  <Input
                    id="kl-ctx-tag"
                    value={doc.contextPanel.tag[lang] ?? ''}
                    disabled={!canEdit}
                    onChange={(e) => setText((d) => d.contextPanel.tag, e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Titlu" htmlFor="kl-ctx-title">
                <Input
                  id="kl-ctx-title"
                  value={doc.contextPanel.title[lang] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setText((d) => d.contextPanel.title, e.target.value)}
                />
              </Field>
              <Field label="Text" htmlFor="kl-ctx-text">
                <Input
                  id="kl-ctx-text"
                  value={doc.contextPanel.text[lang] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setText((d) => d.contextPanel.text, e.target.value)}
                />
              </Field>

              <h3>Subsol și comportament</h3>
              <Field label="Notă" htmlFor="kl-note">
                <Input
                  id="kl-note"
                  value={doc.footer.note[lang] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setText((d) => d.footer.note, e.target.value)}
                />
              </Field>
              <div className="flex wrap">
                <Field label="Ajutor" htmlFor="kl-help">
                  <Select
                    id="kl-help"
                    value={doc.footer.help.mode}
                    disabled={!canEdit}
                    onChange={(e) =>
                      update(
                        (d) => (
                          (d.footer.help.mode = e.target
                            .value as KioskDoc['footer']['help']['mode']),
                          d
                        ),
                      )
                    }
                  >
                    <option value="phone">Telefon</option>
                    <option value="instructions">Instrucțiuni</option>
                    <option value="disabled">Dezactivat</option>
                  </Select>
                </Field>
                {doc.footer.help.mode === 'phone' && (
                  <Field label="Număr" htmlFor="kl-phone">
                    <Input
                      id="kl-phone"
                      value={doc.footer.help.phone ?? ''}
                      disabled={!canEdit}
                      onChange={(e) => update((d) => ((d.footer.help.phone = e.target.value), d))}
                    />
                  </Field>
                )}
                {doc.footer.help.mode === 'instructions' && (
                  <Field label="Instrucțiuni" htmlFor="kl-instr">
                    <Input
                      id="kl-instr"
                      value={doc.footer.help.instructions?.[lang] ?? ''}
                      disabled={!canEdit}
                      onChange={(e) =>
                        update(
                          (d) => (
                            (d.footer.help.instructions = {
                              ...(d.footer.help.instructions ?? {}),
                              [lang]: e.target.value,
                            }),
                            d
                          ),
                        )
                      }
                    />
                  </Field>
                )}
                <Field label="Revenire la inactivitate (s)" htmlFor="kl-idle">
                  <Input
                    id="kl-idle"
                    type="number"
                    min={10}
                    max={600}
                    value={doc.behavior.idleTimeoutSec}
                    disabled={!canEdit}
                    onChange={(e) =>
                      update((d) => ((d.behavior.idleTimeoutSec = Number(e.target.value)), d))
                    }
                  />
                </Field>
              </div>
            </div>
          )}
        </Card>
        <Card>
          <div className="flex between" style={{ marginBottom: 12 }}>
            <h2>Previzualizare</h2>
            <div className="flex">
              <Select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                aria-label="Limba previzualizării"
                style={{ width: 120 }}
              >
                {doc.languages.map((l) => (
                  <option key={l} value={l}>
                    {l.toUpperCase()}
                  </option>
                ))}
              </Select>
              <Select
                value={orientation}
                onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
                aria-label="Orientare"
                style={{ width: 140 }}
              >
                <option value="portrait">Portret</option>
                <option value="landscape">Peisaj</option>
              </Select>
            </div>
          </div>
          <KioskPreview doc={doc} lang={lang} orientation={orientation} />
        </Card>
      </div>
    </>
  );
}

function ActionEditor({
  action: a,
  lang,
  canEdit,
  onChange,
  onRemove,
}: {
  action: KioskAction;
  lang: string;
  canEdit: boolean;
  onChange: (a: KioskAction) => void;
  onRemove?: () => void;
}) {
  const set = (fn: (x: KioskAction) => void) => {
    const n = structuredClone(a);
    fn(n);
    onChange(n);
  };
  const text = (get: (x: KioskAction) => Localized, value: string) =>
    set((x) => ((get(x)[lang] = value), undefined));
  return (
    <div
      style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 14 }}
      className="stack"
    >
      <div className="flex between">
        <strong className="mono small">{a.id}</strong>
        <div className="flex">
          <label className="flex small">
            <input
              type="checkbox"
              checked={a.primary}
              disabled={!canEdit}
              onChange={(e) => set((x) => ((x.primary = e.target.checked), undefined))}
            />{' '}
            principală
          </label>
          {onRemove && canEdit && (
            <Button
              small
              variant="ghost"
              icon="trash"
              aria-label={`Elimină acțiunea ${a.id}`}
              onClick={onRemove}
            />
          )}
        </div>
      </div>
      <div className="flex wrap">
        <Field label="Iconiță" htmlFor={`a-${a.id}-icon`}>
          <Select
            id={`a-${a.id}-icon`}
            value={a.icon}
            disabled={!canEdit}
            onChange={(e) => set((x) => ((x.icon = e.target.value as IconName), undefined))}
          >
            {ICONS.map((i) => (
              <option key={i}>{i}</option>
            ))}
          </Select>
        </Field>
        <Field label="Operație" htmlFor={`a-${a.id}-op`}>
          <Select
            id={`a-${a.id}-op`}
            value={a.flow.operation}
            disabled={!canEdit}
            onChange={(e) =>
              set((x) => ((x.flow.operation = e.target.value as 'deposit' | 'pickup'), undefined))
            }
          >
            <option value="deposit">Depunere</option>
            <option value="pickup">Ridicare</option>
          </Select>
        </Field>
        <Field label="Identificare">
          <div className="flex small">
            {(['numeric', 'qr'] as const).map((m) => (
              <label key={m} className="flex small">
                <input
                  type="checkbox"
                  checked={a.flow.identification.methods.includes(m)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    set(
                      (x) => (
                        (x.flow.identification.methods = e.target.checked
                          ? [...x.flow.identification.methods, m]
                          : x.flow.identification.methods.filter((y) => y !== m)),
                        undefined
                      ),
                    )
                  }
                />
                {m === 'numeric' ? 'cod numeric' : 'QR'}
              </label>
            ))}
          </div>
        </Field>
      </div>
      <Field label="Titlu" htmlFor={`a-${a.id}-title`}>
        <Input
          id={`a-${a.id}-title`}
          value={a.title[lang] ?? ''}
          disabled={!canEdit}
          onChange={(e) => text((x) => x.title, e.target.value)}
        />
      </Field>
      <Field label="Text" htmlFor={`a-${a.id}-text`}>
        <Input
          id={`a-${a.id}-text`}
          value={a.text[lang] ?? ''}
          disabled={!canEdit}
          onChange={(e) => text((x) => x.text, e.target.value)}
        />
      </Field>
      <details>
        <summary className="small muted">Texte și comportament după deschidere</summary>
        <div className="stack" style={{ marginTop: 10 }}>
          {(['success', 'afterClose', 'invalidCode', 'doorFailed', 'offline'] as const).map((k) => (
            <Field key={k} label={k} htmlFor={`a-${a.id}-${k}`}>
              <Input
                id={`a-${a.id}-${k}`}
                value={a.flow.texts[k][lang] ?? ''}
                disabled={!canEdit}
                onChange={(e) => text((x) => x.flow.texts[k], e.target.value)}
              />
            </Field>
          ))}
          <div className="flex wrap small">
            {(['waitDoorClose', 'askFinished', 'allowReopenOnce'] as const).map((k) => (
              <label key={k} className="flex small">
                <input
                  type="checkbox"
                  checked={a.flow.afterOpen[k]}
                  disabled={!canEdit}
                  onChange={(e) =>
                    set((x) => ((x.flow.afterOpen[k] = e.target.checked), undefined))
                  }
                />
                {k}
              </label>
            ))}
          </div>
          <div className="flex wrap">
            <Field label="Ușă deschisă (s)" htmlFor={`a-${a.id}-door`}>
              <Input
                id={`a-${a.id}-door`}
                type="number"
                value={a.flow.timeouts.doorOpenSec}
                disabled={!canEdit}
                onChange={(e) =>
                  set((x) => ((x.flow.timeouts.doorOpenSec = Number(e.target.value)), undefined))
                }
              />
            </Field>
            <Field label="Sesiune (s)" htmlFor={`a-${a.id}-sess`}>
              <Input
                id={`a-${a.id}-sess`}
                type="number"
                value={a.flow.timeouts.sessionSec}
                disabled={!canEdit}
                onChange={(e) =>
                  set((x) => ((x.flow.timeouts.sessionSec = Number(e.target.value)), undefined))
                }
              />
            </Field>
            <Field label="Cifre cod" htmlFor={`a-${a.id}-len`}>
              <Select
                id={`a-${a.id}-len`}
                value={a.flow.identification.numericLength ?? 6}
                disabled={!canEdit}
                onChange={(e) =>
                  set(
                    (x) => (
                      (x.flow.identification.numericLength = Number(e.target.value)),
                      undefined
                    ),
                  )
                }
              >
                <option value={6}>6</option>
                <option value={8}>8</option>
              </Select>
            </Field>
          </div>
        </div>
      </details>
    </div>
  );
}
